import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, X, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";

const CATEGORIES = ["Recetas", "Guías", "Cortes", "Eventos"];

const EMPTY_FORM = {
  title: "", slug: "", excerpt: "", body_md: "",
  cover_image: "", category: "Recetas", tags: "",
  published: true, seo_title: "", seo_description: "",
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | slug
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/admin/blog/posts").then((r) => { setPosts(r.data); setLoading(false); });
  };
  useEffect(load, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY_FORM); setErr(""); };
  const startEdit = (p) => {
    setEditing(p.slug);
    setForm({ ...p, tags: (p.tags || []).join(", ") });
    setErr("");
  };
  const cancel = () => { setEditing(null); setForm(EMPTY_FORM); setErr(""); };

  const save = async () => {
    setErr("");
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      };
      if (editing === "new") {
        await api.post("/admin/blog/posts", payload);
      } else {
        await api.patch(`/admin/blog/posts/${editing}`, payload);
      }
      cancel();
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`¿Eliminar el artículo ${slug}?`)) return;
    await api.delete(`/admin/blog/posts/${slug}`);
    load();
  };

  const togglePublish = async (p) => {
    await api.patch(`/admin/blog/posts/${p.slug}`, { published: !p.published });
    load();
  };

  return (
    <div className="p-8" data-testid="admin-blog">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl tracking-wider uppercase text-white">Blog</h1>
          <p className="text-white/60 text-sm mt-1">{posts.length} artículos</p>
        </div>
        {!editing && (
          <button onClick={startNew} className="btn-arre bg-arre-red text-white text-xs tracking-widest uppercase px-4 py-2 inline-flex items-center gap-2" data-testid="new-post-btn">
            <Plus className="w-4 h-4" /> Nuevo artículo
          </button>
        )}
      </div>

      {editing && (
        <div className="bg-arre-surface border border-arre-border p-6 mb-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-2xl text-white">{editing === "new" ? "Nuevo artículo" : `Editar: ${editing}`}</h2>
            <button onClick={cancel} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="post-title" />
            <Field label="Slug (opcional)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testid="post-slug" placeholder="se genera del título" />
            <div className="md:col-span-2">
              <Field label="Resumen / Excerpt" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} testid="post-excerpt" />
            </div>
            <Field label="Imagen de portada (URL)" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testid="post-cover" />
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Categoría</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white" data-testid="post-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Tags (separados por coma)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testid="post-tags" />
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Estado</label>
              <label className="flex items-center gap-2 text-white py-2">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-arre-red" data-testid="post-published" />
                Publicado
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Cuerpo (Markdown)</label>
              <textarea value={form.body_md} onChange={(e) => setForm({ ...form, body_md: e.target.value })} rows={14} className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white font-mono text-sm" placeholder="## Subtítulo&#10;Tu texto aquí. **negrita** *itálica* [enlace](https://...)&#10;- Item 1&#10;- Item 2" data-testid="post-body" />
            </div>
            <Field label="SEO title (opcional)" value={form.seo_title} onChange={(v) => setForm({ ...form, seo_title: v })} testid="post-seo-title" />
            <Field label="SEO description (opcional)" value={form.seo_description} onChange={(v) => setForm({ ...form, seo_description: v })} testid="post-seo-desc" />
          </div>
          {err && <div className="mt-4 p-3 border border-arre-red bg-arre-red/10 text-sm text-white">{err}</div>}
          <div className="flex gap-3 mt-6 pt-5 border-t border-arre-border">
            <button onClick={save} className="btn-arre bg-arre-red text-white text-xs tracking-widest uppercase px-5 py-2" data-testid="save-post-btn">Guardar</button>
            <button onClick={cancel} className="btn-arre border border-arre-border text-white/70 text-xs tracking-widest uppercase px-5 py-2">Cancelar</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-white/50">Cargando...</div> : (
        <div className="bg-arre-surface border border-arre-border">
          {posts.length === 0 ? (
            <div className="p-10 text-center text-white/40">Sin artículos. ¡Crea el primero!</div>
          ) : (
            posts.map((p) => (
              <div key={p.slug} className="flex items-center justify-between p-4 border-b border-arre-border last:border-0" data-testid={`post-row-${p.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="font-display text-lg text-white truncate">{p.title}</div>
                    {!p.published && <span className="text-[10px] tracking-widest uppercase bg-yellow-900/30 border border-yellow-600/50 text-yellow-400 px-2 py-0.5">Borrador</span>}
                  </div>
                  <div className="text-xs text-white/40 mt-1">{p.category} · {p.slug} · {p.views || 0} lecturas</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => togglePublish(p)} className="p-2 text-white/50 hover:text-arre-red" title={p.published ? "Despublicar" : "Publicar"}>
                    {p.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => startEdit(p)} className="p-2 text-white/50 hover:text-arre-red" data-testid={`edit-${p.slug}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.slug)} className="p-2 text-white/50 hover:text-red-500" data-testid={`del-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white" data-testid={testid} />
    </div>
  );
}
